import { useState } from 'react';
import { useData } from './DataContext.jsx';
import { durationMin, todayIso, capitalizeName, currentAbsence } from './lib/format.js';
import { Avatar, ProgressRing, StatusDot } from './components.jsx';
import { IconFire, IconTrend, IconSettings } from './icons.jsx';
import { MemberWorkSettingsModal } from './member-work-settings-modal.jsx';

function TeamPage({ onNavigate }) {
  const { profiles, projects, timeEntries, projectUsage, absences, isAdmin } = useData();
  const [editProfile, setEditProfile] = useState(null);
  const now = new Date();

  const today = todayIso();
  const todayByUser = {};
  for (const te of timeEntries) {
    if (te.entry_date !== today) continue;
    todayByUser[te.user_id] = (todayByUser[te.user_id] || 0) + durationMin(te.from_time, te.to_time);
  }

  const presentProfiles = profiles.filter(p => !currentAbsence(p.id, absences, now));
  const total = presentProfiles.length;
  const active = presentProfiles.filter(p => (todayByUser[p.id] || 0) > 0).length;
  const criticalProjects = projects.filter(p => {
    const usage = projectUsage[p.id]?.minutes ?? 0;
    return p.budget_minutes && (usage / p.budget_minutes) >= 0.9;
  });

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <h1>Team</h1>
        {total > 0 && (
          <div className="chip chip-ok">
            <span className="dot-status" style={{ background:'var(--ok)' }}/> {active}/{total} heute aktiv
          </div>
        )}
        <div className="topbar-spacer"/>
        <button className="btn"><IconTrend size={13}/> Report</button>
      </div>
      <div className="content">
        {total === 0 ? (
          <div className="card card-pad-lg">
            <div className="tiny muted">Noch keine Team-Mitglieder. Lade Leute unter „Einstellungen → Team" ein.</div>
          </div>
        ) : (
          <>
            <div className="grid grid-12" style={{ marginBottom: 20 }}>
              <div className="card col-4 card-pad-lg">
                <div className="eyebrow">Team-Disziplin heute</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14 }}>
                  <ProgressRing pct={total > 0 ? Math.round(active/total*100) : 0} size={100} stroke={9} accent="var(--ok)">
                    <div className="serif" style={{ fontSize: 22, fontWeight: 500 }}>
                      {total > 0 ? Math.round(active/total*100) : 0}%
                    </div>
                  </ProgressRing>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{active} von {total} haben heute erfasst</div>
                  </div>
                </div>
              </div>
              <div className="card col-4 card-pad-lg">
                <div className="eyebrow">Projekte im kritischen Bereich</div>
                {criticalProjects.length === 0 ? (
                  <div className="tiny muted" style={{ marginTop: 12 }}>Alles im grünen Bereich.</div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    {criticalProjects.map(p => {
                      const pct = Math.round((projectUsage[p.id]?.minutes ?? 0) / p.budget_minutes * 100);
                      return (
                        <div
                          key={p.id}
                          onClick={() => onNavigate?.({ view: 'project', id: p.id })}
                          title={`${p.title} öffnen`}
                          style={{ flex: '1 1 140px', padding: 12, background: 'var(--crit-soft)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, cursor: 'pointer', transition: 'border-color 120ms, background 120ms' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--crit)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
                        >
                          <div className="tiny mono" style={{ color: 'var(--crit)', fontWeight: 600 }}>{pct}%</div>
                          <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{p.title}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="card col-4 card-pad-lg">
                <div className="eyebrow">Gesamt diese Woche</div>
                <div className="serif" style={{ fontSize: 34, fontWeight: 500, marginTop: 10 }}>—</div>
                <div className="tiny muted">Wochen-Aggregation folgt</div>
              </div>
            </div>

            <div className="card card-pad-lg">
              <div className="card-title">
                <span>Tracking-Disziplin · Heute</span>
                <span className="sub">Fokus: vollständige Tage, nicht Gesamtstunden</span>
              </div>
              {profiles.map(u => {
                const todayMin = todayByUser[u.id] || 0;
                const goal = 480;
                const pct = Math.round(todayMin / goal * 100);
                const status = pct >= 100 ? 'complete' : pct > 0 ? 'partial' : 'not_started';
                const goalH = Number(u.weekly_hours ?? 40) / 5;
                const abs = currentAbsence(u.id, absences, now);
                const isAbsent = !!abs;
                const badge = abs ? {
                  emoji: abs.kind === 'sick' ? '🤒' : '🏖',
                  title: abs.kind === 'sick' ? 'Krank' : 'Urlaub',
                } : null;
                return (
                  <div key={u.id} style={{
                    display: 'grid', gridTemplateColumns: '32px 1fr 60px 1fr 110px 90px 36px',
                    alignItems: 'center', padding: '12px 0',
                    borderTop: '1px solid var(--stroke)', gap: 16,
                    opacity: isAbsent ? 0.5 : 1,
                  }}>
                    <Avatar user={u} size={32} badge={badge}/>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                        {capitalizeName(u.full_name) || u.email}
                        {isAbsent && (
                          <span className="tiny muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                            · {abs.kind === 'sick' ? 'krank' : 'im Urlaub'}
                          </span>
                        )}
                      </div>
                      <div className="tiny muted">{u.role === 'admin' ? 'Admin' : 'Member'} · {u.weekly_hours ?? 40}h/Woche</div>
                    </div>
                    <div className="ic" style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 12 }}>
                      <IconFire size={12}/> <span className="mono">{u.streak_days}</span>
                    </div>
                    <div className="bar"><span style={{ width: `${Math.min(100,pct)}%`, background: pct >= 100 ? 'var(--ok)' : pct > 0 ? 'var(--warn)' : 'var(--crit)' }}/></div>
                    <div className="mono" style={{ fontSize: 12.5 }}>{Math.round(todayMin/60*10)/10}h / {goalH}h</div>
                    <div>
                      {isAbsent ? (
                        <span className="tiny muted">abwesend</span>
                      ) : (
                        <><StatusDot status={status}/> <span className="tiny muted" style={{ marginLeft: 4 }}>{status === 'complete' ? 'Komplett' : status === 'partial' ? 'Teilweise' : 'Offen'}</span></>
                      )}
                    </div>
                    {isAdmin ? (
                      <button className="icon-btn" onClick={() => setEditProfile(u)}
                              title="Bearbeiten" style={{ opacity: 1 }}>
                        <IconSettings size={14}/>
                      </button>
                    ) : <div/>}
                  </div>
                );
              })}
            </div>

            {isAdmin && (
              <div className="card card-pad" style={{ marginTop: 20, background: 'var(--bg-sunken)' }}>
                <div className="tiny" style={{ color: 'var(--ink-2)', lineHeight: 1.6 }}>
                  <b>Neuer Mitarbeiter?</b> Lade die Person in Supabase ein:
                  <span className="muted"> Authentication → Users → Invite user → E-Mail-Adresse eintragen.</span>
                  Sobald sie sich über den Magic-Link einloggt, erscheint sie hier und du kannst Stammdaten + Wochenstunden direkt zuweisen.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {editProfile && (
        <MemberWorkSettingsModal
          profile={editProfile}
          onClose={() => setEditProfile(null)}
        />
      )}
    </>
  );
}
export { TeamPage };
